import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import Home from "./pages/home/home";
import ItSolutions from "./pages/services/it-solutions/it-solutions";
import ItConsultingServices from "./pages/services/it-consulting-services/it-consulting-services";
import CloudTransformation from "./pages/services/cloud-transformation/cloud-transformation";
import BusinessAnalytics from "./pages/services/business-analytics/business-analytics";
import ApplicationManagement from "./pages/services/application-management/application-management";
import ErpProcurementAndSupplyChain from "./pages/services/erp-procurement-and-supply-chain/erp-procurement-and-supply-chain";
import DevOpsAndCyberSecurity from "./pages/services/devops-and-cyber-security/devops-and-cyber-security";
import BuildOperateTransfer from "./pages/services/build-operate-transfer/build-operate-transfer";
import About from "./pages/about/about";
import Contact from "./pages/contact/contact";
import NotFound from "./pages/not-found/notFound";

import Navbar from "./components/navbar/navbar";
import Footer from "./components/footer/footer";
import ScrollToTop from "./components/scroll-to-top";

import "./App.css";
import "@fortawesome/fontawesome-free/css/all.min.css";

// ✅ Language Sync — only auto-detect once, respect user selection afterward
function LanguageSync() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const savedLang = localStorage.getItem("selectedLanguage");
    const path = location.pathname;

    // ✅ If URL starts with /de — ensure German is active
    if (path.startsWith("/de") && i18n.language !== "de") {
      i18n.changeLanguage("de");
      localStorage.setItem("selectedLanguage", "de");
      return;
    }

    // ✅ If saved language is German but URL is English — add /de prefix
    if (savedLang === "de" && !path.startsWith("/de")) {
      navigate("/de" + path, { replace: true });
      i18n.changeLanguage("de");
      return;
    }

    // ✅ If saved language is English but URL has /de — remove it
    if (savedLang === "en" && path.startsWith("/de")) {
      navigate(path.replace("/de", "") || "/", { replace: true });
      i18n.changeLanguage("en");
      return;
    }

    // ✅ First-time visit (no localStorage)
    if (!savedLang) {
      if (path.startsWith("/de")) {
        localStorage.setItem("selectedLanguage", "de");
      } else {
        localStorage.setItem("selectedLanguage", "en");
      }
    }
  }, [i18n, location.pathname, navigate]);

  return null;
}

function App() {
  return (
    <Router>
      <Navbar />
      <ScrollToTop />
      <LanguageSync />

      <Routes>
        {/* ✅ English Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/services/it-solutions" element={<ItSolutions />} />
        <Route path="/services/it-consulting-services" element={<ItConsultingServices />} />
        <Route path="/services/cloud-transformation" element={<CloudTransformation />} />
        <Route path="/services/business-analytics" element={<BusinessAnalytics />} />
        <Route path="/services/application-management" element={<ApplicationManagement />} />
        <Route path="/services/erp-procurement-and-supply-chain" element={<ErpProcurementAndSupplyChain />} />
        <Route path="/services/devops-and-cyber-security" element={<DevOpsAndCyberSecurity />} />
        <Route path="/services/build-operate-transfer" element={<BuildOperateTransfer />} />
        <Route path="/contact-us" element={<Contact />} />
        <Route path="/about-us" element={<About />} />

        {/* ✅ German Routes */}
        <Route path="/de" element={<Home />} />
        <Route path="/de/services/it-solutions" element={<ItSolutions />} />
        <Route path="/de/services/it-consulting-services" element={<ItConsultingServices />} />
        <Route path="/de/services/cloud-transformation" element={<CloudTransformation />} />
        <Route path="/de/services/business-analytics" element={<BusinessAnalytics />} />
        <Route path="/de/services/application-management" element={<ApplicationManagement />} />
        <Route path="/de/services/erp-procurement-and-supply-chain" element={<ErpProcurementAndSupplyChain />} />
        <Route path="/de/services/devops-and-cyber-security" element={<DevOpsAndCyberSecurity />} />
        <Route path="/de/services/build-operate-transfer" element={<BuildOperateTransfer />} />
        <Route path="/de/contact-us" element={<Contact />} />
        <Route path="/de/about-us" element={<About />} />

        <Route path="*" element={<NotFound />} />
      </Routes>

      <Footer />
    </Router>
  );
}

export default App;
